import { Typography } from '@iheartjane/reefer';
import { formatCurrency } from '@iheartjane/utils';
import { clsx } from 'clsx';
import { useMemo } from 'react';

import { formatWeightLabel } from '@jane/bloom/utils/strings/formatWeightLabel';
import type { SpecialsViewModel } from '@jane/bloom/viewModels/specials';

import styles from './bulkOrCartTotalProgress.module.css';
import { DealQualifiedBanner } from './dealQualifiedBanner';

const buildProgressText = (
  currentAmount: number,
  targetAmount: number,
  specialType: 'cart_total' | 'bulk_pricing'
): {
  completeProgress: string;
  currentProgress: string;
} => {
  let formattedCurrentAmount, formattedTargetAmount;
  if (specialType === 'bulk_pricing') {
    formattedCurrentAmount = formatWeightLabel(currentAmount);
    formattedTargetAmount = formatWeightLabel(targetAmount);
  } else {
    formattedCurrentAmount = formatCurrency(currentAmount);
    formattedTargetAmount = formatCurrency(targetAmount);
  }

  const currentProgress = `${formattedCurrentAmount} of ${formattedTargetAmount}`;
  const completeProgress = `${formattedTargetAmount} of ${formattedTargetAmount}`;

  return { completeProgress, currentProgress };
};

const ProgressBar = ({
  currentAmount,
  targetAmount,
}: {
  currentAmount: number;
  targetAmount: number;
}) => {
  const progress = useMemo(
    () => Math.min(100, (currentAmount / targetAmount) * 100),
    [currentAmount, targetAmount]
  );

  return (
    <div className={styles['progressBar']}>
      <div
        className={styles['progressFill']}
        style={{ width: `${progress}%` }}
      ></div>
    </div>
  );
};

type BulkOrCartTotalProgressProps = {
  specialsProducts: SpecialsViewModel;
  specialType: 'cart_total' | 'bulk_pricing';
};

export const BulkOrCartTotalProgress = ({
  specialsProducts,
  specialType,
}: BulkOrCartTotalProgressProps) => {
  const currentAmount =
    specialType === 'bulk_pricing'
      ? specialsProducts.currentWeight
      : specialsProducts.currentAmount;
  const targetAmount =
    specialType === 'bulk_pricing'
      ? specialsProducts.targetWeight
      : specialsProducts.targetAmount;
  const { completeProgress, currentProgress } = buildProgressText(
    currentAmount,
    targetAmount,
    specialType
  );
  return (
    <div className={styles['progressContainer']}>
      <div
        className={clsx(styles['title'], {
          [styles['titleQualified']]:
            specialsProducts.isUnlocked || specialsProducts.isApplied,
        })}
      >
        <Typography color="inherit" variant="header-bold">
          {specialsProducts.qualifyingStatusMsg}
        </Typography>
      </div>

      <ProgressBar currentAmount={currentAmount} targetAmount={targetAmount} />

      <div className={styles['currentProgress']}>
        {specialsProducts.isUnlocked || specialsProducts.isApplied
          ? completeProgress
          : currentProgress}
      </div>

      {specialsProducts.isApplied && specialsProducts.discountedAmount && (
        <div className={styles['dealQualifiedBannerSection']}>
          <DealQualifiedBanner
            discountedAmount={specialsProducts.discountedAmount}
          />
        </div>
      )}
    </div>
  );
};
